import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  doc,
  getDoc,
} from "firebase/firestore";

import { db } from "../../firebase";

const text = (value) =>
  String(value ?? "").trim();

const escapeHtml = (value) =>
  text(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const formatDateTime = (value) => {
  if (!value) return "-";

  const date = typeof value?.toDate === "function" ? value.toDate() : value?.seconds ? new Date(value.seconds * 1000) : new Date(value);

  if (
    Number.isNaN(date.getTime())
  ) {
    return text(value) || "-";
  }

  return date.toLocaleString(
    "en-IN",
    {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }
  );
};

const getImage = (
  guest,
  side
) => {
  if (!guest) return "";

  if (side === "front") {
    return (
      guest.idFrontUrl ||
      guest.idFrontPhotoUrl ||
      guest.idFrontPhoto ||
      guest.idFrontImageUrl ||
      guest.frontImage ||
      guest.frontUrl ||
      ""
    );
  }

  return (
    guest.idBackUrl ||
    guest.idBackPhotoUrl ||
    guest.idBackPhoto ||
    guest.idBackImageUrl ||
    guest.backImage ||
    guest.backUrl ||
    ""
  );
};

const normalizeGuests = (stay) => {
  if (
    Array.isArray(stay?.guests) &&
    stay.guests.length
  ) {
    return stay.guests;
  }

  if (
    Array.isArray(
      stay?.guestProfiles
    ) &&
    stay.guestProfiles.length
  ) {
    return stay.guestProfiles;
  }

  return [
    {
      isPrimary: true,
      name:
        stay?.guestName || "",
      phoneNumber:
        stay?.phoneNumber || "",
      age: stay?.age || "",
      gender:
        stay?.gender || "",
      nationality:
        stay?.nationality || "",
      address:
        stay?.address || "",
      idType:
        stay?.idType || "",
      idNumber:
        stay?.idNumber || "",
      idFrontUrl:
        stay?.idFrontUrl || "",
      idBackUrl:
        stay?.idBackUrl || "",
      relation:
        "Primary Guest",
    },
  ];
};

function RegistrationCardModal({
  stay,
  hotelName,
  onClose,
}) {
  const baseGuests =
    useMemo(
      () =>
        normalizeGuests(stay),
      [stay]
    );

  const [
    guests,
    setGuests,
  ] = useState(baseGuests);

  const [
    profilesLoading,
    setProfilesLoading,
  ] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadFullGuestProfiles =
      async () => {
        setGuests(baseGuests);

        if (!stay) {
          return;
        }

        const profileIds =
          Array.from(
            new Set(
              [
                ...(Array.isArray(
                  stay?.guestProfileIds
                )
                  ? stay.guestProfileIds
                  : []),

                ...baseGuests.map(
                  (guest) =>
                    guest?.profileId
                ),

                stay?.primaryGuestId,
              ]
                .map(text)
                .filter(Boolean)
            )
          );

        if (
          profileIds.length === 0
        ) {
          return;
        }

        const needsProfileLoad =
          baseGuests.some(
            (guest) =>
              !getImage(
                guest,
                "front"
              ) ||
              !getImage(
                guest,
                "back"
              ) ||
              !text(
                guest?.idNumber
              )
          );

        if (!needsProfileLoad) {
          return;
        }

        setProfilesLoading(true);

        try {
          const profileDocs =
            await Promise.all(
              profileIds.map(
                async (
                  profileId
                ) => {
                  try {
                    const snap =
                      await getDoc(
                        doc(
                          db,
                          "guestProfiles",
                          profileId
                        )
                      );

                    return snap.exists()
                      ? {
                          profileId:
                            snap.id,
                          ...snap.data(),
                        }
                      : null;
                  } catch (
                    error
                  ) {
                    console.error(
                      "Guest profile load error:",
                      error
                    );
                    return null;
                  }
                }
              )
            );

          if (cancelled) {
            return;
          }

          const profiles =
            profileDocs.filter(
              Boolean
            );

          const merged =
            baseGuests.map(
              (guest, index) => {
                const profile =
                  profiles.find(
                    (item) =>
                      text(
                        item?.profileId
                      ) ===
                      text(
                        guest?.profileId
                      )
                  ) ||
                  (index === 0
                    ? profiles.find(
                        (item) =>
                          text(
                            item?.profileId
                          ) ===
                          text(
                            stay?.primaryGuestId
                          )
                      )
                    : null);

                return profile
                  ? {
                      ...profile,
                      ...guest,
                      age:
                        guest?.age ||
                        profile?.age ||
                        "",
                      gender:
                        guest?.gender ||
                        profile?.gender ||
                        "",
                      nationality:
                        guest?.nationality ||
                        profile?.nationality ||
                        "",
                      address:
                        guest?.address ||
                        profile?.address ||
                        "",
                      idType:
                        guest?.idType ||
                        profile?.idType ||
                        "",
                      idNumber:
                        guest?.idNumber ||
                        profile?.idNumber ||
                        "",
                      idFrontUrl:
                        getImage(
                          guest,
                          "front"
                        ) ||
                        getImage(
                          profile,
                          "front"
                        ),
                      idBackUrl:
                        getImage(
                          guest,
                          "back"
                        ) ||
                        getImage(
                          profile,
                          "back"
                        ),
                    }
                  : guest;
              }
            );

          setGuests(merged);
        } finally {
          if (!cancelled) {
            setProfilesLoading(
              false
            );
          }
        }
      };

    loadFullGuestProfiles();

    return () => {
      cancelled = true;
    };
  }, [
    baseGuests,
    stay?.guestProfileIds,
    stay?.primaryGuestId,
  ]);

  if (!stay) {
    return null;
  }

  const printDocument = (
    idsOnly = false
  ) => {
    const printWindow =
      window.open(
        "",
        "_blank",
        "width=1100,height=850"
      );

    if (!printWindow) {
      alert(
        "Browser popup block kar raha hai. Popups allow karo."
      );
      return;
    }

    const guestHtml = guests
      .map((guest, index) => {
        const frontImage =
          getImage(
            guest,
            "front"
          );

        const backImage =
          getImage(
            guest,
            "back"
          );

        return `
        <section class="guest">
          <div class="guest-title">
            Guest ${index + 1}
            ${
              index === 0 ||
              guest.isPrimary
                ? '<span>PRIMARY</span>'
                : ""
            }
          </div>

          ${
            idsOnly
              ? `
          <div class="details ids-only-details">
            ${detail(
              "Name",
              guest.name ||
                guest.guestName
            )}

            ${detail(
              "ID Type",
              guest.idType
            )}

            ${detail(
              "ID Number",
              guest.idNumber
            )}
          </div>
          `
              : `
          <div class="details">
            ${detail(
              "Name",
              guest.name ||
                guest.guestName
            )}

            ${detail(
              "Phone",
              guest.phoneNumber ||
                guest.phone
            )}

            ${detail(
              "Age",
              guest.age
            )}

            ${detail(
              "Gender",
              guest.gender
            )}

            ${detail(
              "Nationality",
              guest.nationality
            )}

            ${detail(
              "Relation",
              index === 0
                ? "Primary Guest"
                : guest.relation
            )}

            ${detail(
              "ID Type",
              guest.idType
            )}

            ${detail(
              "ID Number",
              guest.idNumber
            )}
          </div>

          <div class="address">
            <span>Address</span>
            <b>${escapeHtml(
              guest.address || "-"
            )}</b>
          </div>
          `
          }

          <div class="ids">
            ${imageBox(
              "ID Front",
              frontImage
            )}

            ${imageBox(
              "ID Back",
              backImage
            )}
          </div>

          <div class="guest-sign">
            Guest ${index + 1}
            Signature:
            ______________________
          </div>
        </section>
      `;
      })
      .join("");

    printWindow.document.write(`
      <!DOCTYPE html>

      <html>
      <head>
        <title>
          ${escapeHtml(
            stay.room || "Room"
          )} Registration
        </title>

        <style>
          * {
            box-sizing:border-box;
          }

          body {
            font-family:Arial,sans-serif;
            margin:0;
            padding:20px;
            color:#111827;
          }

          .page {
            max-width:1000px;
            margin:auto;
          }

          .hotel {
            text-align:center;
            border-bottom:2px solid #111;
            padding-bottom:12px;
            margin-bottom:15px;
          }

          .hotel h1 {
            margin:0;
          }

          .hotel p {
            margin:5px 0 0;
          }

          .stay {
            display:grid;
            grid-template-columns:
              repeat(4,1fr);
            gap:7px;
            margin-bottom:15px;
          }

          .field,
          .details > div,
          .address {
            border:1px solid #cbd5e1;
            padding:8px;
          }

          .field span,
          .details span,
          .address span {
            display:block;
            font-size:10px;
            color:#64748b;
            margin-bottom:4px;
          }

          .guest {
            border:2px solid #475569;
            border-radius:8px;
            padding:12px;
            margin-bottom:15px;
            page-break-inside:avoid;
          }

          .guest-title {
            font-size:18px;
            font-weight:800;
            margin-bottom:10px;
          }

          .guest-title span {
            background:#dcfce7;
            color:#166534;
            font-size:9px;
            padding:4px 7px;
            border-radius:20px;
            margin-left:7px;
          }

          .details {
            display:grid;
            grid-template-columns:
              repeat(4,1fr);
            gap:6px;
          }

          .ids-only-details {
            grid-template-columns:
              repeat(3,1fr);
            margin-bottom:10px;
          }

          .address {
            margin-top:6px;
          }

          .ids {
            display:grid;
            grid-template-columns:
              1fr 1fr;
            gap:12px;
            margin-top:12px;
          }

          .id-box {
            border:1px solid #94a3b8;
            padding:7px;
            min-height:250px;
          }

          .id-box h3 {
            margin:0 0 6px;
            text-align:center;
            font-size:13px;
          }

          .id-box img {
            width:100%;
            height:230px;
            object-fit:contain;
          }

          .no-image {
            height:220px;
            display:flex;
            align-items:center;
            justify-content:center;
            color:#94a3b8;
          }

          .guest-sign {
            margin-top:35px;
            font-size:11px;
          }

          .declaration {
            border:1px solid #94a3b8;
            padding:10px;
            margin-top:12px;
            line-height:1.5;
            font-size:11px;
          }

          .signatures {
            display:flex;
            justify-content:space-between;
            margin-top:45px;
          }

          @page {
            size:A4 portrait;
            margin:10mm;
          }

          @media print {
            body {
              padding:0;
            }
          }
        </style>
      </head>

      <body>
        <main class="page">

          <header class="hotel">
            <h1>
              ${escapeHtml(
                hotelName ||
                  stay.hotelName ||
                  "Hotel"
              )}
            </h1>

            <p>
              ${
                idsOnly
                  ? "GUEST ID DOCUMENTS"
                  : "GUEST REGISTRATION CARD"
              }
            </p>
          </header>

          ${
            idsOnly
              ? ""
              : `
          <section class="stay">
            ${stayField(
              "Room",
              stay.room
            )}

            ${stayField(
              "A&D Serial",
              stay.adRegisterSerialNo ||
                stay.adSerialNo
            )}

            ${stayField(
              "Check-In",
              formatDateTime(
                stay.checkInAt
              )
            )}

            ${stayField(
              "Expected Checkout",
              formatDateTime(
                stay.expectedCheckOutAt
              )
            )}

            ${stayField(
              "Adults",
              stay.adults ||
                guests.length
            )}

            ${stayField(
              "Children",
              stay.children || 0
            )}

            ${stayField(
              "Tariff",
              `₹${Number(
                stay.tariff || 0
              )}`
            )}

            ${stayField(
              "Advance",
              `₹${Number(
                stay.advance || 0
              )}`
            )}
          </section>
          `
          }

          ${guestHtml}

          ${
            idsOnly
              ? ""
              : `
            <div class="declaration">
              I hereby declare that
              the above information
              and identity documents
              provided by me are true
              and correct. I agree to
              comply with the hotel
              rules during my stay.
            </div>

            <div class="signatures">
              <div>
                Primary Guest Signature
                <br/><br/>
                ______________________
              </div>

              <div>
                Reception / Manager
                <br/><br/>
                ______________________
              </div>
            </div>
          `
          }

        </main>

        <script>
          const imgs =
            Array.from(
              document.images
            );

          Promise.all(
            imgs.map((img) => {
              if (img.complete) {
                return Promise.resolve();
              }

              return new Promise(
                (resolve) => {
                  img.onload = resolve;
                  img.onerror = resolve;
                }
              );
            })
          ).then(() => {
            setTimeout(
              () => window.print(),
              400
            );
          });
        </script>
      </body>
      </html>
    `);

    printWindow.document.close();
  };

  return (
    <div
      style={overlayStyle}
      onMouseDown={(event) => {
        if (
          event.target ===
          event.currentTarget
        ) {
          onClose();
        }
      }}
    >
      <div style={modalStyle}>
        <div style={headerStyle}>
          <div>
            <h2
              style={{
                margin: 0,
              }}
            >
              Guest Registration & IDs
            </h2>

            <div
              style={{
                marginTop: 5,
                color: "#64748b",
              }}
            >
              Room {stay.room} •{" "}
              {guests.length} Guest
              {guests.length > 1
                ? "s"
                : ""}

              {profilesLoading
                ? " • Loading saved IDs..."
                : ""}
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            style={closeButton}
          >
            ×
          </button>
        </div>

        <div style={guestListStyle}>
          {guests.map(
            (guest, index) => {
              const frontImage =
                getImage(
                  guest,
                  "front"
                );

              const backImage =
                getImage(
                  guest,
                  "back"
                );

              return (
                <div
                  key={
                    guest.profileId ||
                    index
                  }
                  style={
                    guestCardStyle
                  }
                >
                  <div
                    style={
                      guestTitleStyle
                    }
                  >
                    {index === 0
                      ? "Primary Guest"
                      : `Guest ${
                          index + 1
                        }`}
                  </div>

                  <div
                    style={
                      detailsGrid
                    }
                  >
                    <Info
                      label="Name"
                      value={
                        guest.name
                      }
                    />

                    <Info
                      label="Phone"
                      value={
                        guest.phoneNumber
                      }
                    />

                    <Info
                      label="Age"
                      value={
                        guest.age
                      }
                    />

                    <Info
                      label="Nationality"
                      value={
                        guest.nationality
                      }
                    />

                    <Info
                      label="ID"
                      value={
                        guest.idType
                      }
                    />

                    <Info
                      label="ID No."
                      value={
                        guest.idNumber
                      }
                    />
                  </div>

                  <div
                    style={
                      previewGrid
                    }
                  >
                    <ImagePreview
                      title="ID Front"
                      url={frontImage}
                    />

                    <ImagePreview
                      title="ID Back"
                      url={backImage}
                    />
                  </div>
                </div>
              );
            }
          )}
        </div>

        <div style={buttonGrid}>
          <button
            type="button"
            onClick={() =>
              printDocument(false)
            }
            style={greenButton}
          >
            🖨 Print Registration Card
          </button>

          <button
            type="button"
            onClick={() =>
              printDocument(true)
            }
            style={blueButton}
          >
            🪪 Print IDs Only
          </button>
        </div>
      </div>
    </div>
  );
}

function Info({
  label,
  value,
}) {
  return (
    <div style={infoStyle}>
      <small>{label}</small>

      <strong>
        {text(value) || "-"}
      </strong>
    </div>
  );
}

function ImagePreview({
  title,
  url,
}) {
  return (
    <div style={imageCard}>
      <strong>{title}</strong>

      {url ? (
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
        >
          <img
            src={url}
            alt={title}
            style={imageStyle}
          />
        </a>
      ) : (
        <div style={noImage}>
          No ID image
        </div>
      )}
    </div>
  );
}

function detail(label, value) {
  return `
    <div>
      <span>${escapeHtml(
        label
      )}</span>
      <b>${escapeHtml(
        value || "-"
      )}</b>
    </div>
  `;
}

function stayField(
  label,
  value
) {
  return `
    <div class="field">
      <span>${escapeHtml(
        label
      )}</span>

      <b>${escapeHtml(
        value || "-"
      )}</b>
    </div>
  `;
}

function imageBox(
  title,
  url
) {
  return `
    <div class="id-box">
      <h3>${escapeHtml(
        title
      )}</h3>

      ${
        url
          ? `<img
              src="${escapeHtml(
                url
              )}"
              alt="${escapeHtml(
                title
              )}"
            />`
          : `<div class="no-image">
              No ID image
            </div>`
      }
    </div>
  `;
}

const overlayStyle = {
  position: "fixed",
  inset: 0,
  zIndex: 25000,
  background: "rgba(15,23,42,.76)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 14,
};

const modalStyle = {
  width: "min(1000px,100%)",
  maxHeight: "95vh",
  overflowY: "auto",
  background: "#fff",
  borderRadius: 18,
  padding: 18,
};

const headerStyle = {
  display: "flex",
  justifyContent: "space-between",
  gap: 15,
};

const closeButton = {
  width: 38,
  height: 38,
  border: 0,
  borderRadius: 9,
  fontSize: 24,
  cursor: "pointer",
};

const guestListStyle = {
  display: "grid",
  gap: 12,
  marginTop: 16,
};

const guestCardStyle = {
  border: "1px solid #cbd5e1",
  borderRadius: 13,
  padding: 13,
  background: "#f8fafc",
};

const guestTitleStyle = {
  fontSize: 17,
  fontWeight: 900,
  marginBottom: 10,
};

const detailsGrid = {
  display: "grid",
  gridTemplateColumns:
    "repeat(auto-fit,minmax(140px,1fr))",
  gap: 8,
};

const infoStyle = {
  background: "#fff",
  border: "1px solid #e2e8f0",
  borderRadius: 8,
  padding: 8,
  display: "grid",
  gap: 4,
};

const previewGrid = {
  display: "grid",
  gridTemplateColumns:
    "repeat(auto-fit,minmax(250px,1fr))",
  gap: 10,
  marginTop: 10,
};

const imageCard = {
  background: "#fff",
  border: "1px solid #cbd5e1",
  borderRadius: 10,
  padding: 9,
};

const imageStyle = {
  width: "100%",
  height: 210,
  objectFit: "contain",
  marginTop: 7,
};

const noImage = {
  height: 180,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#94a3b8",
};

const buttonGrid = {
  display: "grid",
  gridTemplateColumns:
    "repeat(auto-fit,minmax(210px,1fr))",
  gap: 10,
  marginTop: 15,
};

const greenButton = {
  border: 0,
  borderRadius: 10,
  padding: 12,
  background: "#16a34a",
  color: "#fff",
  fontWeight: 900,
  cursor: "pointer",
};

const blueButton = {
  ...greenButton,
  background: "#2563eb",
};

export default RegistrationCardModal;